import { Skeleton, Stack, Typography } from '@mui/material';
import { formatter } from '@repo/utils';
import { COLORS } from '../../constants';
import { InterestFreePill } from '../paymentSchedule';

/** Interest line under the count: green pill when interest-free, grey text when not, nothing when unknown. */
export const InterestLabel = ({ withInterest }: { withInterest?: boolean }) =>
  withInterest === false ? (
    <InterestFreePill />
  ) : withInterest ? (
    <Typography component="span" variant="subtitle2" sx={{ color: COLORS.text.light }}>
      Con interés
    </Typography>
  ) : null;

const planAmountSx = {
  fontSize: 16,
  lineHeight: '22px',
  fontWeight: 700,
  color: COLORS.text.main,
  whiteSpace: 'nowrap',
} as const;

/**
 * Radio label for one plan, same row as customer-mobile: count and interest on the left, monthly amount and total
 * on the right. The row wraps so big amounts drop the right column below instead of clipping on narrow screens.
 * Inner columns are spans because the shared RadioGroup stretches every nested div to full width.
 */
export const PlanOption = ({
  count,
  amount,
  finalAmount,
  withInterest,
}: {
  count: number;
  amount: number;
  finalAmount?: number;
  withInterest?: boolean;
}) => (
  <Stack
    component="span"
    direction="row"
    sx={{ alignItems: 'center', flexWrap: 'wrap', columnGap: 2, rowGap: 0.5, width: '100%' }}
  >
    <Stack component="span" sx={{ flex: '1 1 auto', gap: 0.5, alignItems: 'flex-start', minWidth: 0 }}>
      <Typography component="span" sx={planAmountSx}>
        {count} {count === 1 ? 'cuota' : 'cuotas'}
      </Typography>
      <InterestLabel withInterest={withInterest} />
    </Stack>
    <Stack component="span" sx={{ ml: 'auto', gap: 0.25, alignItems: 'flex-end' }}>
      <Typography component="span" sx={planAmountSx}>
        {formatter(amount.toString(), 'currency')}
      </Typography>
      <Typography component="span" variant="subtitle2" sx={{ whiteSpace: 'nowrap' }}>
        Total: {formatter((finalAmount ?? amount * count).toString(), 'currency')}
      </Typography>
    </Stack>
  </Stack>
);

/** Placeholder for a PlanOption row with its radio, while the plans are recalculated */
export const PlanOptionSkeleton = () => (
  <Stack direction="row" sx={{ alignItems: 'center', gap: 2, py: 1.5 }}>
    <Skeleton variant="circular" animation="wave" sx={{ width: 24, height: 24, mx: 1, flexShrink: 0 }} />
    <Stack sx={{ flex: 1, gap: 0.75 }}>
      <Skeleton variant="rounded" animation="wave" sx={{ width: 88, height: 20 }} />
      <Skeleton variant="rounded" animation="wave" sx={{ width: 64, height: 14 }} />
    </Stack>
    <Stack sx={{ gap: 0.75, alignItems: 'flex-end' }}>
      <Skeleton variant="rounded" animation="wave" sx={{ width: 96, height: 20 }} />
      <Skeleton variant="rounded" animation="wave" sx={{ width: 112, height: 14 }} />
    </Stack>
  </Stack>
);
